import { Badge, DataTable, ErrorState, Loading, formatDate, formatMoney } from "@corecrm/ui";
import { Link, useNavigate } from "react-router-dom";
import { useProjects, useQuotes } from "../api/queries";
import type { QuoteSummary } from "../api/types";
import { Status } from "../components/bits";
import { QUOTE_KIND } from "../lib/labels";

/** Each quote's current version, newest first. The prototype's pQuotes(). */
export function QuotesPage() {
  const quotes = useQuotes();
  const projects = useProjects();
  const navigate = useNavigate();
  const projectName = (id: string) => projects.data?.find((p) => p.id === id)?.name ?? "your project";
  if (quotes.isPending) return <Loading />;
  if (quotes.error) return <ErrorState error={quotes.error} onRetry={() => quotes.refetch()} />;
  return (
    <DataTable<QuoteSummary>
      label="Quotes"
      rows={quotes.data}
      rowKey={(q) => q.id}
      onRowClick={(q) => navigate(`/quotes/${q.id}`)}
      empty="No quotes yet."
      columns={[
        {
          header: "Quote",
          cell: (q) => (
            <>
              <span className="mono">
                {q.number} v{q.version_number}
              </span>
              {q.kind !== "standard" && (
                <span className="sub">
                  <Badge kind="acc">{QUOTE_KIND[q.kind]}</Badge>
                </span>
              )}
            </>
          ),
        },
        {
          header: "For",
          cell: (q) =>
            q.kind === "change_request" && q.project_id ? (
              <>
                {q.title}
                <span className="sub">Adds to {projectName(q.project_id)}</span>
              </>
            ) : (
              q.title
            ),
        },
        { header: "Status", cell: (q) => <Status status={q.status} /> },
        {
          header: "Total",
          align: "right",
          cell: (q) => (
            <span className="mono">
              {formatMoney(q.total)}
              {!!q.monthly_total && q.monthly_total.amount_minor > 0 && (
                <span className="sub">+ {formatMoney(q.monthly_total)}/mo</span>
              )}
            </span>
          ),
        },
        { header: "Valid until", cell: (q) => formatDate(q.valid_until) },
        {
          header: "",
          role: "ctl",
          align: "right",
          cell: (q) => (
            <Link
              className={q.can_respond ? "btn sm primary" : "btn sm"}
              to={`/quotes/${q.id}`}
              onClick={(e) => e.stopPropagation()}
            >
              {q.can_respond ? "Review" : "View"}
            </Link>
          ),
        },
      ]}
    />
  );
}
